import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import { Loader2, ShieldCheck, ChevronRight, ArrowRight } from "lucide-react";

/**
 * AdminFilings — admin queue for the white-glove 8949 workflow.
 *
 * Access: env-driven allow-list (ADMIN_EMAILS) — controlled by `is_admin`
 * on the /auth/me payload. Non-admins see an "access denied" screen.
 *
 * The page shows counts per status at the top, then a filtered list of
 * filings the admin can transition to the next state (with an optional
 * CPA note that gets emailed to the creator).
 */

const STATUSES = [
  "submitted",
  "in_review",
  "drafting",
  "ready_for_signature",
  "filed",
  "done",
  "cancelled",
];

const STATUS_LABEL = {
  submitted: "Submitted",
  in_review: "In review",
  drafting: "Drafting",
  ready_for_signature: "Ready for sig",
  filed: "Filed",
  done: "Done",
  cancelled: "Cancelled",
};

// Suggested next state per current state (admin can still pick anything).
const NEXT_STATE = {
  submitted: "in_review",
  in_review: "drafting",
  drafting: "ready_for_signature",
  ready_for_signature: "filed",
  filed: "done",
  done: "done",
  cancelled: "cancelled",
};

const AdminFilings = () => {
  const { user } = useAuth();
  const [data, setData] = useState({ filings: [], counts: {} });
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const url = filter ? `/admin/filings?status=${filter}` : "/admin/filings";
      const { data } = await api.get(url);
      setData(data);
    } catch (e) {
      toast.error("Couldn't load admin queue");
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const total = useMemo(
    () => Object.values(data.counts || {}).reduce((a, b) => a + b, 0),
    [data.counts]
  );

  if (!user?.is_admin) {
    return (
      <div data-testid="admin-filings-page">
        <PageHeader overline="Admin" title="White-glove filings" testId="admin-filings-header" />
        <div className="p-8 max-w-xl" data-testid="admin-denied">
          <div className="border border-[#2c241e] rounded-xl p-6 bg-[#1f1815]">
            <div className="text-[10px] uppercase tracking-[0.22em] text-rose-400">Access denied</div>
            <p className="text-sm text-zinc-300 mt-2">
              This page is restricted to Quill CPA / admin accounts. If you were meant to have access,
              ask your team lead to add your email to <span className="font-mono">ADMIN_EMAILS</span>.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="admin-filings-page">
      <PageHeader
        overline="Admin · Concierge queue"
        title="White-glove filings"
        description={`Manage the Studio-tier 8949 workflow. ${total} filing${total === 1 ? "" : "s"} across all states.`}
        testId="admin-filings-header"
      />

      {/* Filter chips */}
      <div className="px-4 md:px-8 pt-4 md:pt-6 flex flex-wrap gap-2" data-testid="admin-filter-chips">
        <FilterChip label={`All (${total})`} active={!filter} onClick={() => setFilter("")} testId="filter-all" />
        {STATUSES.map((s) => (
          <FilterChip
            key={s}
            label={`${STATUS_LABEL[s]} (${data.counts?.[s] || 0})`}
            active={filter === s}
            onClick={() => setFilter(s)}
            testId={`filter-${s}`}
          />
        ))}
      </div>

      {/* List */}
      <div className="p-4 md:p-8 space-y-4">
        {loading ? (
          <div className="flex items-center gap-3 text-zinc-500 text-sm">
            <Loader2 size={14} className="animate-spin" /> Loading queue…
          </div>
        ) : data.filings.length === 0 ? (
          <div className="border border-dashed border-[#2c241e] rounded-xl p-8 text-center text-zinc-500 text-sm" data-testid="admin-empty">
            No filings in this state.
          </div>
        ) : (
          data.filings.map((f) => (
            <AdminFilingCard key={f.filing_request_id} filing={f} onUpdated={load} />
          ))
        )}
      </div>
    </div>
  );
};

const FilterChip = ({ label, active, onClick, testId }) => (
  <button
    type="button" onClick={onClick} data-testid={testId}
    className="text-xs uppercase tracking-[0.18em] px-3 py-1.5 rounded-full border transition-colors"
    style={
      active
        ? { background: "hsl(var(--quill-warm))", color: "hsl(var(--quill-ink-warm))", borderColor: "hsl(var(--quill-warm))" }
        : { borderColor: "#2c241e", color: "#a89e93" }
    }
  >
    {label}
  </button>
);

const AdminFilingCard = ({ filing, onUpdated }) => {
  const [note, setNote] = useState("");
  const [next, setNext] = useState(NEXT_STATE[filing.status] || filing.status);
  const [saving, setSaving] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  const transition = async () => {
    if (next === filing.status) {
      toast.error("Pick a different status to transition to.");
      return;
    }
    setSaving(true);
    try {
      await api.patch(`/admin/filings/${filing.filing_request_id}`, {
        status: next,
        note: note.trim(),
      });
      toast.success(`Moved to ${STATUS_LABEL[next]}. Creator will be emailed.`);
      setNote("");
      await onUpdated();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Transition failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="border border-[#2c241e] rounded-xl bg-[#1f1815]"
      data-testid={`admin-filing-${filing.filing_request_id}`}
    >
      <div className="p-4 md:p-6 grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6">
        {/* Info */}
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500">
            TY {filing.tax_year} · {filing.email}
          </div>
          <div className="font-display text-lg tracking-tight mt-1">{filing.legal_name}</div>
          <div className="text-xs text-zinc-500 mt-1">
            {filing.entity_type.replace(/_/g, " ")} · state {filing.filing_state || "—"} · SSN/EIN ****{filing.tax_id_last4}
          </div>
          <div className="text-[11px] font-mono text-zinc-600 mt-2">{filing.filing_request_id}</div>
          <div className="text-[11px] text-zinc-500 mt-1">submitted {fmtDate(filing.submitted_at)}</div>
        </div>

        {/* Wallets + notes */}
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 mb-2">Wallets</div>
          {(filing.wallet_addresses || []).length === 0 ? (
            <div className="text-xs text-zinc-500">— none submitted —</div>
          ) : (
            <ul className="space-y-1" data-testid={`admin-wallets-${filing.filing_request_id}`}>
              {filing.wallet_addresses.map((w, i) => (
                <li key={i} className="text-xs font-mono text-zinc-300 truncate">
                  <span className="text-amber-300/80 mr-1.5">{w.chain?.toUpperCase()}</span>
                  {w.address}
                  {w.label && <span className="text-zinc-500 ml-2">({w.label})</span>}
                </li>
              ))}
            </ul>
          )}
          {filing.notes && (
            <div className="mt-3">
              <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 mb-1">Creator note</div>
              <div className="text-xs text-zinc-300 whitespace-pre-wrap">{filing.notes}</div>
            </div>
          )}
        </div>

        {/* Transition */}
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 mb-2">Status</div>
          <div className="flex items-center gap-2 mb-3">
            <span className="text-sm text-white">{STATUS_LABEL[filing.status]}</span>
            <ArrowRight size={12} className="text-zinc-500" />
            <select
              value={next} onChange={(e) => setNext(e.target.value)}
              className="bg-[#1a1410] border border-[#2c241e] px-2 py-1.5 rounded-md text-sm"
              data-testid={`admin-next-${filing.filing_request_id}`}
            >
              {STATUSES.map((s) => (
                <option key={s} value={s}>{STATUS_LABEL[s]}</option>
              ))}
            </select>
          </div>
          <textarea
            value={note} onChange={(e) => setNote(e.target.value)}
            rows={2} placeholder="CPA note (emailed to creator)"
            className="w-full bg-[#1a1410] border border-[#2c241e] px-2 py-2 rounded-md text-sm"
            data-testid={`admin-note-${filing.filing_request_id}`}
          />
          <button
            type="button" onClick={transition} disabled={saving || next === filing.status}
            className="mt-2 w-full inline-flex items-center justify-center gap-2 bg-white text-black text-sm font-medium px-4 py-2 rounded-md hover:bg-zinc-200 disabled:opacity-50"
            data-testid={`admin-transition-${filing.filing_request_id}`}
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <ShieldCheck size={14} />}
            Move to {STATUS_LABEL[next]}
          </button>
        </div>
      </div>

      <button
        type="button" onClick={() => setShowHistory((h) => !h)}
        className="w-full border-t border-[#2c241e] py-2 text-[11px] uppercase tracking-[0.22em] text-zinc-500 hover:text-white hover:bg-[#241c17]"
        data-testid={`admin-history-toggle-${filing.filing_request_id}`}
      >
        {showHistory ? "Hide history" : "History"} <ChevronRight size={10} className={`inline transition-transform ${showHistory ? "rotate-90" : ""}`} />
      </button>
      {showHistory && (
        <div className="border-t border-[#2c241e] p-4 md:p-6 space-y-2 text-sm">
          {(filing.status_history || []).slice().reverse().map((h, i) => (
            <div key={i} className="flex items-start gap-3">
              <div className="w-2 h-2 rounded-full bg-amber-300/80 mt-1.5 shrink-0" />
              <div className="flex-1 min-w-0">
                <div>{STATUS_LABEL[h.status] || h.status} <span className="text-xs text-zinc-500 ml-2">{fmtDate(h.at)}</span></div>
                {h.note && <div className="text-xs text-zinc-500 mt-0.5">{h.note}</div>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminFilings;
